import { useEffect, useMemo, useState } from 'react'
import { apiJson } from '../api'
import { pageStyles } from '../styles'
import type { ThemeTokens } from '../theme'

export interface DirectoryUser {
  id: string
  displayName?: string
  mail?: string
  userPrincipalName?: string
  department?: string
}

export function dirEmail(u: DirectoryUser): string {
  return u.mail ?? u.userPrincipalName ?? ''
}

export function dirLabel(u: DirectoryUser): string {
  return u.displayName ?? dirEmail(u) ?? u.id
}

interface DirectoryPickerCommonProps {
  tokens: ThemeTokens
  placeholder?: string
  /** Ids hidden entirely from results -- already chosen elsewhere, so the
   *  same person can't be added twice. */
  excludeIds?: string[]
  disabled?: boolean
}

interface DirectoryPickerSingleProps extends DirectoryPickerCommonProps {
  multiple?: false
  onPick: (user: DirectoryUser) => void
}

interface DirectoryPickerMultiProps extends DirectoryPickerCommonProps {
  multiple: true
  /** Adds every currently-ticked result in one call, in the order shown. */
  onPickMany: (users: DirectoryUser[]) => void
  /** Ids shown in the list but disabled and labelled `disabledLabel` --
   *  people who exist in the directory but cannot be picked (e.g. already a
   *  CHU-GPT user). Unlike `excludeIds`, these stay visible. */
  disabledIds?: string[]
  disabledLabel?: string
}

type DirectoryPickerProps = DirectoryPickerSingleProps | DirectoryPickerMultiProps

/**
 * A debounced Entra directory type-ahead: search the tenant via `/api/users`,
 * pick a real person. Reused by onboarding, the leaderboard exclude list, and
 * admin-role assignment — nobody types raw emails. `exclude` hides ids already
 * chosen elsewhere so the same person can't be added twice.
 *
 * `multiple` (Round 18, 25 Sep 2026, Maroun: "it would be good if I can
 * select multiple people to onboard") keeps the results open with one
 * checkbox per row instead of adding-and-closing on a single click: tick
 * several, then "Add N selected" hands them all to `onPickMany` at once.
 * Every OTHER caller keeps passing `onPick` and gets the original
 * click-one-row-and-close behaviour unchanged.
 */
export function DirectoryPicker(props: DirectoryPickerProps) {
  const { tokens, placeholder = 'Search your directory by name or email…', excludeIds = [], disabled = false } = props
  const styles = useMemo(() => pageStyles(tokens), [tokens])
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<DirectoryUser[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

  useEffect(() => {
    const q = query.trim()
    // A fresh search invalidates any in-progress selection from the last
    // result set -- "N selected" must never count a row no longer on screen.
    setSelectedIds(new Set())
    if (q.length < 2) {
      setResults([])
      setLoading(false)
      return
    }
    let cancelled = false
    setLoading(true)
    const timer = setTimeout(() => {
      void (async () => {
        try {
          const data = await apiJson<{ users: DirectoryUser[] }>(`/api/users?q=${encodeURIComponent(q)}`)
          if (!cancelled) setResults(data.users)
        } catch {
          if (!cancelled) setResults([])
        } finally {
          if (!cancelled) setLoading(false)
        }
      })()
    }, 250)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [query])

  const candidates = results.filter((u) => !excludeIds.includes(u.id))

  if (props.multiple) {
    const { onPickMany, disabledIds = [], disabledLabel = 'Already in CHU-GPT' } = props
    const selectableIds = candidates.filter((u) => !disabledIds.includes(u.id)).map((u) => u.id)
    const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedIds.has(id))

    function toggle(id: string) {
      if (disabledIds.includes(id)) return
      setSelectedIds((prev) => {
        const next = new Set(prev)
        if (next.has(id)) next.delete(id)
        else next.add(id)
        return next
      })
    }
    function selectAll() {
      setSelectedIds(new Set(selectableIds))
    }
    function clearSelection() {
      setSelectedIds(new Set())
    }
    function addSelected() {
      const chosen = candidates.filter((u) => selectedIds.has(u.id))
      if (chosen.length === 0) return
      onPickMany(chosen)
      setQuery('')
      setResults([])
      setSelectedIds(new Set())
    }

    return (
      <div style={styles.pickerWrap}>
        <input
          aria-label="Search directory"
          style={{ ...styles.input, minWidth: '20rem' }}
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
        />
        {query.trim().length >= 2 ? (
          <div style={styles.pickerDropdown}>
            {loading ? (
              <div style={styles.pickerOptionMeta}>Searching…</div>
            ) : candidates.length === 0 ? (
              <div style={styles.pickerOptionMeta}>No matches in the directory.</div>
            ) : (
              <>
                {candidates.map((u) => {
                  const isDisabled = disabledIds.includes(u.id)
                  return (
                    <label
                      key={u.id}
                      style={{
                        ...styles.pickerOptionRow,
                        ...(isDisabled ? styles.pickerOptionRowDisabled : null),
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedIds.has(u.id)}
                        disabled={isDisabled}
                        onChange={() => toggle(u.id)}
                      />
                      <span style={{ display: 'flex', flexDirection: 'column', gap: '0.1rem' }}>
                        <span>{dirLabel(u)}</span>
                        <span style={styles.pickerOptionMeta}>
                          {dirEmail(u)}
                          {u.department ? ` · ${u.department}` : ''}
                        </span>
                      </span>
                      {isDisabled ? <span style={styles.pickerTag}>{disabledLabel}</span> : null}
                    </label>
                  )
                })}
                <div style={styles.pickerFooter}>
                  <span>
                    {selectedIds.size} selected
                    {selectableIds.length > 0 && !allSelected ? (
                      <>
                        {' '}
                        ·{' '}
                        <button type="button" style={styles.linkButton} onClick={selectAll}>
                          Select all {selectableIds.length}
                        </button>
                      </>
                    ) : null}
                  </span>
                  <span style={{ display: 'flex', gap: '0.5rem' }}>
                    <button type="button" style={styles.buttonGhost} onClick={clearSelection} disabled={selectedIds.size === 0}>
                      Clear
                    </button>
                    <button type="button" style={styles.button} onClick={addSelected} disabled={selectedIds.size === 0}>
                      Add {selectedIds.size} selected
                    </button>
                  </span>
                </div>
              </>
            )}
          </div>
        ) : null}
      </div>
    )
  }

  const { onPick } = props

  return (
    <div style={styles.pickerWrap}>
      <input
        aria-label="Search directory"
        style={{ ...styles.input, minWidth: '20rem' }}
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
      />
      {query.trim().length >= 2 ? (
        <div style={styles.pickerDropdown}>
          {loading ? (
            <div style={styles.pickerOptionMeta}>Searching…</div>
          ) : candidates.length === 0 ? (
            <div style={styles.pickerOptionMeta}>No matches in the directory.</div>
          ) : (
            candidates.map((u) => (
              <button
                key={u.id}
                type="button"
                style={styles.pickerOption}
                onClick={() => {
                  onPick(u)
                  setQuery('')
                  setResults([])
                }}
              >
                <span>{dirLabel(u)}</span>
                <span style={styles.pickerOptionMeta}>
                  {dirEmail(u)}
                  {u.department ? ` · ${u.department}` : ''}
                </span>
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  )
}
